import { Currency } from "@repo/i18n"
import { useForm } from "@tanstack/react-form"
import { Link } from "@tanstack/react-router"
import { ArrowLeft, Loader2, Plus, Save, Sparkles, Trash2 } from "lucide-react"
import { PageHeader, Section } from "@/components/layout"
import { BACK_LINK_CLASS } from "@/components/promotions/back-link"
import { Button } from "@/components/ui/button"
import { DatePicker } from "@/components/ui/date-picker"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Separator } from "@/components/ui/separator"
import { Switch } from "@/components/ui/switch"
import { Textarea } from "@/components/ui/textarea"
import { useExchangeRates } from "@/hooks/use-exchange-rates"
import type { AdminCoupon } from "@/types/marketplace.types"
import { cn } from "@/utils/styles.helpers"

// Form values use user-friendly units (dollars, not cents).
// Conversion to/from cents happens at the boundary (getDefaultValues / onSubmit).

export interface CurrencyAmountFormValues {
	_key?: string
	currency: Currency
	amount: number
	minimumOrderAmount: number | null
	maximumDiscountAmount: number | null
}

let _nextKey = 0
/** Stable React key for a freshly added per-currency row. */
export function genKey(): string {
	return `ca_${++_nextKey}`
}

export type CouponAudienceFormValue = "everyone" | "restricted"

export interface CouponFormValues {
	code: string
	description: string
	type: "percentage" | "fixed_amount"
	value: number
	currency: Currency | null
	region: "NA" | "EU" | null
	applicableCurrencies: Currency[]
	currencyAmounts: CurrencyAmountFormValues[]
	maxRedemptions: number | null
	maxRedemptionsPerUser: number
	minimumOrderAmount: number | null
	maximumDiscountAmount: number | null
	startsAt: Date
	expiresAt: Date | null
	newBuyersOnly: boolean
	accountMustPredateCoupon: boolean
	audience: CouponAudienceFormValue
}

interface CouponFormProps {
	mode: "create" | "edit"
	initialValues?: Partial<CouponFormValues>
	coupon?: AdminCoupon | null
	onSubmit: (values: CouponFormValues) => Promise<void>
	isSubmitting: boolean
}

export const REGION_OPTIONS = [
	{ value: "NA", label: "North America" },
	{ value: "EU", label: "Europe" },
]

export const AUDIENCE_OPTIONS: {
	value: CouponAudienceFormValue
	label: string
}[] = [
	{ value: "everyone", label: "Everyone" },
	{ value: "restricted", label: "Specific users only" },
]

export const MASTER_CURRENCY = "EUR"

export const CURRENCY_OPTIONS: { value: Currency; label: string }[] = [
	{ value: Currency.EUR, label: "EUR (\u20ac)" },
	{ value: Currency.USD, label: "USD ($)" },
	{ value: Currency.CAD, label: "CAD (CA$)" },
	{ value: Currency.GBP, label: "GBP (\u00a3)" },
	{ value: Currency.CHF, label: "CHF" },
	{ value: Currency.SEK, label: "SEK" },
	{ value: Currency.DKK, label: "DKK" },
	{ value: Currency.NOK, label: "NOK" },
	{ value: Currency.PLN, label: "PLN" },
	{ value: Currency.HUF, label: "HUF" },
]

// Rounding step (in major units) for each currency when converting from EUR.
// Produces clean prices instead of raw FX output like "$10.73".
const ROUNDING_STEPS: Record<string, number> = {
	EUR: 0.5,
	USD: 0.5,
	CAD: 0.5,
	GBP: 0.5,
	CHF: 0.5,
	SEK: 5,
	DKK: 5,
	NOK: 5,
	PLN: 1,
	HUF: 100,
}

function roundToNice(
	eurAmount: number,
	rate: number,
	currency: string,
): number {
	const raw = eurAmount * rate
	const step = ROUNDING_STEPS[currency] ?? 0.5
	return Math.round(raw / step) * step
}

function toEurEquivalent(amount: number, rate: number): number {
	return amount / rate
}

function formatEurHint(amount: number): string {
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "EUR",
		maximumFractionDigits: 2,
	}).format(amount)
}

const CURRENCY_SYMBOLS: Record<string, string> = {
	USD: "$",
	CAD: "CA$",
	EUR: "\u20ac",
	GBP: "\u00a3",
	CHF: "CHF",
	SEK: "kr",
	DKK: "kr",
	NOK: "kr",
	PLN: "z\u0142",
	HUF: "Ft",
}

function getCurrencySymbol(currency: string | null): string {
	if (!currency) return "$"
	return CURRENCY_SYMBOLS[currency] ?? currency
}

export function centsToDollars(cents: number | null): number | null {
	if (cents === null) return null
	return cents / 100
}

export function dollarsToCents(dollars: number | null): number | null {
	if (dollars === null) return null
	return Math.round(dollars * 100)
}

/** Converts per-currency form rows (major units) to API rows (cents). */
export function currencyAmountsToCents(rows: CurrencyAmountFormValues[]) {
	return rows.map(({ _key, ...ca }) => ({
		...ca,
		amount: Math.round(ca.amount * 100),
		minimumOrderAmount: dollarsToCents(ca.minimumOrderAmount),
		maximumDiscountAmount: dollarsToCents(ca.maximumDiscountAmount),
	}))
}

/** Converts API per-currency rows (cents) to form rows (major units). */
export function currencyAmountsFromCents(
	rows: {
		currency: Currency
		amount: number
		minimumOrderAmount?: number | null
		maximumDiscountAmount?: number | null
	}[],
): CurrencyAmountFormValues[] {
	return rows.map((ca) => ({
		_key: genKey(),
		currency: ca.currency,
		amount: ca.amount / 100,
		minimumOrderAmount: centsToDollars(ca.minimumOrderAmount ?? null),
		maximumDiscountAmount: centsToDollars(ca.maximumDiscountAmount ?? null),
	}))
}

// Reorders rows so EUR is first if present, but never inserts a new row.
// Avoids silently mutating saved data for coupons that never opted into
// per-currency configuration.
export function reorderEurFirst(
	rows: CurrencyAmountFormValues[],
): CurrencyAmountFormValues[] {
	const eurIndex = rows.findIndex((r) => r.currency === MASTER_CURRENCY)
	if (eurIndex <= 0) return rows
	const reordered = [...rows]
	const [eur] = reordered.splice(eurIndex, 1)
	return [eur, ...reordered]
}

function getDefaultValues(
	mode: "create" | "edit",
	coupon?: AdminCoupon | null,
	initialValues?: Partial<CouponFormValues>,
): CouponFormValues {
	if (mode === "edit" && coupon) {
		let currencyAmounts: CurrencyAmountFormValues[] = []

		if (coupon.currencyAmounts && coupon.currencyAmounts.length > 0) {
			currencyAmounts = coupon.currencyAmounts.map((ca) => ({
				_key: genKey(),
				currency: ca.currency,
				amount: ca.amount / 100,
				minimumOrderAmount: centsToDollars(ca.minimumOrderAmount ?? null),
				maximumDiscountAmount: centsToDollars(ca.maximumDiscountAmount ?? null),
			}))
		} else if (coupon.type === "fixed_amount" && coupon.currency) {
			// Legacy: migrate single currency+value to currencyAmounts for display
			currencyAmounts = [
				{
					_key: genKey(),
					currency: coupon.currency,
					amount: coupon.value / 100,
					minimumOrderAmount: centsToDollars(coupon.minimumOrderAmount),
					maximumDiscountAmount: centsToDollars(coupon.maximumDiscountAmount),
				},
			]
		} else if (
			coupon.type === "percentage" &&
			(coupon.minimumOrderAmount != null ||
				coupon.maximumDiscountAmount != null)
		) {
			// Legacy: percentage coupon with top-level min/max but no currencyAmounts.
			// Surface those constraints as per-currency rows so the admin can see
			// and edit them instead of silently re-saving stale values.
			const legacyCurrencies =
				coupon.applicableCurrencies.length > 0
					? coupon.applicableCurrencies
					: coupon.currency
						? [coupon.currency]
						: []
			currencyAmounts = legacyCurrencies.map((curr) => ({
				_key: genKey(),
				currency: curr,
				amount: 0,
				minimumOrderAmount: centsToDollars(coupon.minimumOrderAmount),
				maximumDiscountAmount: centsToDollars(coupon.maximumDiscountAmount),
			}))
		}

		return {
			code: coupon.code,
			description: coupon.description,
			type: coupon.type,
			value: coupon.type === "fixed_amount" ? coupon.value / 100 : coupon.value,
			currency: coupon.currency,
			region: coupon.region,
			applicableCurrencies: coupon.applicableCurrencies,
			currencyAmounts: reorderEurFirst(currencyAmounts),
			maxRedemptions: coupon.maxRedemptions,
			maxRedemptionsPerUser: coupon.maxRedemptionsPerUser,
			minimumOrderAmount: centsToDollars(coupon.minimumOrderAmount),
			maximumDiscountAmount: centsToDollars(coupon.maximumDiscountAmount),
			startsAt: new Date(coupon.startsAt),
			expiresAt: coupon.expiresAt ? new Date(coupon.expiresAt) : null,
			newBuyersOnly: coupon.newBuyersOnly,
			accountMustPredateCoupon: coupon.accountMustPredateCoupon,
			audience: coupon.audience,
		}
	}

	const base: CouponFormValues = {
		code: "",
		description: "",
		type: "percentage",
		value: 0,
		currency: null,
		region: null,
		applicableCurrencies: [],
		currencyAmounts: [],
		maxRedemptions: null,
		maxRedemptionsPerUser: 1,
		minimumOrderAmount: null,
		maximumDiscountAmount: null,
		startsAt: new Date(),
		expiresAt: null,
		newBuyersOnly: false,
		accountMustPredateCoupon: false,
		audience: "everyone",
		...initialValues,
	}
	return { ...base, currencyAmounts: reorderEurFirst(base.currencyAmounts) }
}

export function CouponForm({
	mode,
	initialValues,
	coupon,
	onSubmit,
	isSubmitting,
}: CouponFormProps) {
	const { rates } = useExchangeRates()

	const form = useForm({
		defaultValues: getDefaultValues(mode, coupon, initialValues),
		onSubmit: async ({ value }) => {
			// Convert dollar values back to cents for the API
			await onSubmit({
				...value,
				value:
					value.type === "fixed_amount"
						? Math.round(value.value * 100)
						: value.value,
				currencyAmounts: currencyAmountsToCents(value.currencyAmounts),
				minimumOrderAmount: dollarsToCents(value.minimumOrderAmount),
				maximumDiscountAmount: dollarsToCents(value.maximumDiscountAmount),
			})
		},
	})

	return (
		<form
			onSubmit={(e) => {
				e.preventDefault()
				form.handleSubmit()
			}}
			className="flex flex-col gap-6"
		>
			<div className="flex flex-col gap-2">
				<Link to="/coupons" search={{} as never} className={BACK_LINK_CLASS}>
					<ArrowLeft />
					Coupons
				</Link>
				<PageHeader
					title={
						mode === "create" ? "Create coupon" : `Edit coupon: ${coupon?.code}`
					}
					description={
						mode === "create"
							? "Create a new promotional coupon"
							: "Update coupon configuration"
					}
					actions={
						<>
							<Button variant="outline" type="button" asChild>
								<Link to="/coupons" search={{} as never}>
									Cancel
								</Link>
							</Button>
							<Button type="submit" disabled={isSubmitting}>
								{isSubmitting ? <Loader2 className="animate-spin" /> : <Save />}
								{mode === "create" ? "Create coupon" : "Save changes"}
							</Button>
						</>
					}
				/>
			</div>

			<div className="grid grid-cols-1 gap-x-12 gap-y-8 lg:grid-cols-2">
				{/* Basic Information */}
				<Section label="Basic information">
					<div className="flex flex-col gap-4">
						<form.Field name="code">
							{(field) => (
								<div className="space-y-2">
									<Label htmlFor="code">
										Coupon Code <span className="text-destructive">*</span>
									</Label>
									<Input
										id="code"
										value={field.state.value}
										onChange={(e) =>
											field.handleChange(e.target.value.toUpperCase())
										}
										onBlur={field.handleBlur}
										placeholder="e.g. SUMMER2026"
										disabled={mode === "edit"}
										maxLength={50}
										className={cn(mode === "edit" && "bg-muted")}
									/>
									<p className="text-xs text-muted-foreground">
										{mode === "edit"
											? "Coupon code cannot be changed after creation"
											: "Unique code customers will enter at checkout. Automatically uppercased."}
									</p>
								</div>
							)}
						</form.Field>

						<form.Field name="description">
							{(field) => (
								<div className="space-y-2">
									<Label htmlFor="description">
										Description <span className="text-destructive">*</span>
									</Label>
									<Textarea
										id="description"
										value={field.state.value}
										onChange={(e) => field.handleChange(e.target.value)}
										onBlur={field.handleBlur}
										placeholder="Brief description of this coupon's purpose"
										rows={3}
										maxLength={500}
									/>
									<p className="text-xs text-muted-foreground">
										Internal description for admin reference
									</p>
								</div>
							)}
						</form.Field>
					</div>
				</Section>

				{/* Discount Configuration */}
				<Section label="Discount configuration">
					<div className="flex flex-col gap-4">
						<form.Field name="type">
							{(field) => (
								<div className="space-y-2">
									<Label>
										Discount Type <span className="text-destructive">*</span>
									</Label>
									<Select
										value={field.state.value}
										onValueChange={(val) =>
											field.handleChange(val as "percentage" | "fixed_amount")
										}
									>
										<SelectTrigger>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="percentage">
												Percentage Discount
											</SelectItem>
											<SelectItem value="fixed_amount">
												Fixed Amount Discount
											</SelectItem>
										</SelectContent>
									</Select>
								</div>
							)}
						</form.Field>

						{/* Percentage value - only shown for percentage type */}
						<form.Subscribe selector={(state) => state.values.type}>
							{(type) =>
								type === "percentage" ? (
									<form.Field name="value">
										{(field) => (
											<div className="space-y-2">
												<Label htmlFor="value">
													Percentage <span className="text-destructive">*</span>
												</Label>
												<div className="relative">
													<Input
														id="value"
														type="number"
														min={0}
														max={100}
														step={1}
														value={field.state.value || ""}
														onChange={(e) =>
															field.handleChange(
																e.target.value === ""
																	? 0
																	: Number(e.target.value),
															)
														}
														onBlur={field.handleBlur}
														placeholder="e.g. 10"
													/>
													<span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
														%
													</span>
												</div>
												<p className="text-xs text-muted-foreground">
													Percentage off the order subtotal (1-100)
												</p>
											</div>
										)}
									</form.Field>
								) : null
							}
						</form.Subscribe>
					</div>
				</Section>

				<CurrencyAmountsCard form={form} rates={rates} />

				{/* Targeting & Restrictions */}
				<Section label="Targeting and restrictions">
					<div className="flex flex-col gap-4">
						<form.Field name="audience">
							{(field) => (
								<div className="space-y-2">
									<Label>Audience</Label>
									<Select
										value={field.state.value}
										onValueChange={(val) =>
											field.handleChange(val as CouponAudienceFormValue)
										}
										disabled={!!coupon?.programId}
									>
										<SelectTrigger
											className={cn(coupon?.programId && "bg-muted")}
										>
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{AUDIENCE_OPTIONS.map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-xs text-muted-foreground">
										{coupon?.programId
											? "Program-issued coupons are always bound to their recipient."
											: field.state.value === "restricted"
												? "Only users on the eligible list can redeem. Add users after saving."
												: "Any user can redeem this coupon."}
									</p>
								</div>
							)}
						</form.Field>

						<Separator />

						<form.Field name="region">
							{(field) => (
								<div className="space-y-2">
									<Label>Region</Label>
									<Select
										value={field.state.value ?? "all"}
										onValueChange={(val) =>
											field.handleChange(
												val === "all" ? null : (val as "NA" | "EU"),
											)
										}
									>
										<SelectTrigger>
											<SelectValue placeholder="All regions" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="all">All Regions</SelectItem>
											{REGION_OPTIONS.map((opt) => (
												<SelectItem key={opt.value} value={opt.value}>
													{opt.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-xs text-muted-foreground">
										Limit coupon to a specific marketplace region
									</p>
								</div>
							)}
						</form.Field>

						<Separator />

						<form.Field name="newBuyersOnly">
							{(field) => (
								<div className="flex items-center justify-between">
									<div className="space-y-0.5">
										<Label>New Buyers Only</Label>
										<p className="text-xs text-muted-foreground">
											Only allow users who have never completed a purchase
										</p>
									</div>
									<Switch
										checked={field.state.value}
										onCheckedChange={field.handleChange}
									/>
								</div>
							)}
						</form.Field>

						<Separator />

						<form.Field name="accountMustPredateCoupon">
							{(field) => (
								<div className="flex items-center justify-between">
									<div className="space-y-0.5">
										<Label>Account must predate coupon</Label>
										<p className="text-xs text-muted-foreground">
											Only allow users whose account was created before the
											coupon's start date. Prevents redeeming the coupon from a
											fresh account.
										</p>
									</div>
									<Switch
										checked={field.state.value}
										onCheckedChange={field.handleChange}
									/>
								</div>
							)}
						</form.Field>
					</div>
				</Section>

				{/* Usage Limits */}
				<Section label="Usage limits">
					<div className="flex flex-col gap-4">
						<form.Field name="maxRedemptions">
							{(field) => (
								<div className="space-y-2">
									<Label htmlFor="maxRedemptions">
										Maximum Total Redemptions
									</Label>
									<Input
										id="maxRedemptions"
										type="number"
										min={1}
										value={field.state.value ?? ""}
										onChange={(e) =>
											field.handleChange(
												e.target.value === "" ? null : Number(e.target.value),
											)
										}
										onBlur={field.handleBlur}
										placeholder="Unlimited"
									/>
									<p className="text-xs text-muted-foreground">
										Total number of times this coupon can be used across all
										users. Leave empty for unlimited.
									</p>
								</div>
							)}
						</form.Field>

						<form.Field name="maxRedemptionsPerUser">
							{(field) => (
								<div className="space-y-2">
									<Label htmlFor="maxRedemptionsPerUser">
										Redemptions Per User
									</Label>
									<Input
										id="maxRedemptionsPerUser"
										type="number"
										min={1}
										value={field.state.value}
										onChange={(e) =>
											field.handleChange(
												e.target.value === "" ? 1 : Number(e.target.value),
											)
										}
										onBlur={field.handleBlur}
									/>
									<p className="text-xs text-muted-foreground">
										How many times a single user can use this coupon
									</p>
								</div>
							)}
						</form.Field>
					</div>
				</Section>

				{/* Schedule */}
				<Section label="Schedule" className="lg:col-span-2">
					<div className="flex flex-col gap-4">
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
							<form.Field name="startsAt">
								{(field) => (
									<div className="space-y-2">
										<Label>
											Start Date <span className="text-destructive">*</span>
										</Label>
										<DatePicker
											value={field.state.value}
											onChange={(date) =>
												field.handleChange(date ?? new Date())
											}
											placeholder="Select start date"
										/>
										<p className="text-xs text-muted-foreground">
											When the coupon becomes available for use
										</p>
									</div>
								)}
							</form.Field>

							<form.Field name="expiresAt">
								{(field) => (
									<div className="space-y-2">
										<Label>Expiry Date</Label>
										<div className="flex gap-2">
											<DatePicker
												value={field.state.value ?? undefined}
												onChange={(date) => field.handleChange(date ?? null)}
												placeholder="No expiry"
											/>
											{field.state.value && (
												<Button
													type="button"
													variant="ghost"
													size="sm"
													onClick={() => field.handleChange(null)}
													className="shrink-0"
												>
													Clear
												</Button>
											)}
										</div>
										<p className="text-xs text-muted-foreground">
											When the coupon expires. Leave empty for no expiry.
										</p>
									</div>
								)}
							</form.Field>
						</div>
					</div>
				</Section>
			</div>
		</form>
	)
}

type DiscountType = "percentage" | "fixed_amount"

interface CurrencyAmountsFormState {
	values: { type: DiscountType; currencyAmounts: CurrencyAmountFormValues[] }
}

interface CurrencyAmountsFieldApi {
	state: { value: CurrencyAmountFormValues[] }
	pushValue: (value: CurrencyAmountFormValues) => void
	removeValue: (index: number) => void
}

/**
 * Per-currency amounts editor shared by the coupon and coupon program forms.
 * The form must own a `currencyAmounts` array field and a `type` field.
 */
export function CurrencyAmountsCard({
	form,
	rates,
	className,
}: {
	// biome-ignore lint/suspicious/noExplicitAny: TanStack Form generic is deeply nested
	form: any
	rates: Record<string, number> | null
	className?: string
}) {
	return (
		<Section
			className={cn("lg:col-span-2", className)}
			label={
				<form.Subscribe
					selector={(state: CurrencyAmountsFormState) => state.values.type}
				>
					{(type: DiscountType) =>
						type === "fixed_amount"
							? "Currency amounts"
							: "Per-currency settings"
					}
				</form.Subscribe>
			}
		>
			<form.Field name="currencyAmounts" mode="array">
				{(field: CurrencyAmountsFieldApi) => (
					<div className="space-y-3">
						<form.Subscribe
							selector={(state: CurrencyAmountsFormState) => state.values.type}
						>
							{(type: DiscountType) => (
								<>
									<p className="text-xs text-muted-foreground">
										{type === "fixed_amount"
											? "Set the discount amount per currency. Buyers checking out in a currency not listed here will not be able to use this coupon."
											: "Optionally configure per-currency minimum order amounts and maximum discount caps. If no currencies are configured, the coupon works for all currencies without min/max constraints."}
									</p>

									{field.state.value.length > 0 && (
										<div className="space-y-3">
											{/* Header row */}
											<div className="grid grid-cols-[140px_1fr_1fr_1fr_40px] gap-2 items-end">
												<Label className="text-xs text-muted-foreground">
													Currency
												</Label>
												{type === "fixed_amount" ? (
													<Label className="text-xs text-muted-foreground">
														Discount Amount *
													</Label>
												) : (
													<div />
												)}
												<Label className="text-xs text-muted-foreground">
													Min. Order
												</Label>
												<Label className="text-xs text-muted-foreground">
													Max Discount
												</Label>
												<div />
											</div>

											{field.state.value.map(
												(row: CurrencyAmountFormValues, i: number) => (
													<CurrencyAmountRow
														key={row._key ?? i}
														form={form}
														index={i}
														showAmount={type === "fixed_amount"}
														usedCurrencies={field.state.value
															.filter(
																(__: CurrencyAmountFormValues, j: number) =>
																	j !== i,
															)
															.map(
																(ca: CurrencyAmountFormValues) => ca.currency,
															)}
														isMaster={row.currency === MASTER_CURRENCY}
														rates={rates}
														onRemove={() => field.removeValue(i)}
													/>
												),
											)}
										</div>
									)}

									<div className="flex items-center gap-2">
										<Button
											type="button"
											variant="outline"
											size="sm"
											onClick={() =>
												field.pushValue({
													_key: genKey(),
													currency:
														CURRENCY_OPTIONS.find(
															(opt) =>
																!field.state.value.some(
																	(ca: CurrencyAmountFormValues) =>
																		ca.currency === opt.value,
																),
														)?.value ?? Currency.USD,
													amount: 0,
													minimumOrderAmount: null,
													maximumDiscountAmount: null,
												})
											}
											disabled={
												field.state.value.length >= CURRENCY_OPTIONS.length
											}
										>
											<Plus />
											Add Currency
										</Button>

										<Button
											type="button"
											variant="secondary"
											size="sm"
											disabled={!rates}
											title={
												rates
													? "Fill other currencies by converting from EUR using live rates, rounded to clean numbers"
													: "Exchange rates are loading..."
											}
											onClick={() => {
												if (!rates) return
												const eurRow = field.state.value.find(
													(r: CurrencyAmountFormValues) =>
														r.currency === MASTER_CURRENCY,
												)
												if (!eurRow) return
												field.state.value.forEach(
													(row: CurrencyAmountFormValues, i: number) => {
														if (row.currency === MASTER_CURRENCY) return
														const rate = rates[row.currency]
														if (!rate) return

														if (type === "fixed_amount" && eurRow.amount > 0) {
															form.setFieldValue(
																`currencyAmounts[${i}].amount`,
																roundToNice(eurRow.amount, rate, row.currency),
															)
														}
														if (eurRow.minimumOrderAmount != null) {
															form.setFieldValue(
																`currencyAmounts[${i}].minimumOrderAmount`,
																roundToNice(
																	eurRow.minimumOrderAmount,
																	rate,
																	row.currency,
																),
															)
														}
														if (eurRow.maximumDiscountAmount != null) {
															form.setFieldValue(
																`currencyAmounts[${i}].maximumDiscountAmount`,
																roundToNice(
																	eurRow.maximumDiscountAmount,
																	rate,
																	row.currency,
																),
															)
														}
													},
												)
											}}
										>
											<Sparkles />
											Apply EUR to all
										</Button>
									</div>
								</>
							)}
						</form.Subscribe>
					</div>
				)}
			</form.Field>
		</Section>
	)
}

// biome-ignore lint/suspicious/noExplicitAny: form type is complex
function CurrencyAmountRow({
	form,
	index,
	showAmount,
	usedCurrencies,
	isMaster,
	rates,
	onRemove,
}: {
	// biome-ignore lint/suspicious/noExplicitAny: TanStack Form generic is deeply nested
	form: any
	index: number
	showAmount: boolean
	usedCurrencies: string[]
	isMaster: boolean
	rates: Record<string, number> | null
	onRemove: () => void
}) {
	const availableCurrencies = CURRENCY_OPTIONS.filter(
		(opt) => !usedCurrencies.includes(opt.value),
	)

	// Subscribe to the whole row instead of individual nested form.Field components.
	// This avoids stale subscriptions when removeValue shifts array indices.
	return (
		<form.Subscribe
			// biome-ignore lint/suspicious/noExplicitAny: TanStack Form state
			selector={(state: any) => state.values.currencyAmounts[index]}
		>
			{(row: CurrencyAmountFormValues | undefined) => {
				if (!row) return null
				const currency = row.currency

				const setField = (
					field: keyof CurrencyAmountFormValues,
					value: unknown,
				) => {
					form.setFieldValue(`currencyAmounts[${index}].${field}`, value)
				}

				const rate = !isMaster && rates ? (rates[currency] ?? null) : null
				const eurHint = (value: number | null): string | null => {
					if (isMaster || rate == null || value == null || value <= 0)
						return null
					return `\u2248 ${formatEurHint(toEurEquivalent(value, rate))}`
				}

				return (
					<div className="grid grid-cols-[140px_1fr_1fr_1fr_40px] gap-2 items-start">
						<div className="space-y-1">
							<Select
								value={currency}
								onValueChange={(val) => setField("currency", val)}
								disabled={isMaster}
							>
								<SelectTrigger className={cn("h-9", isMaster && "bg-muted")}>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{availableCurrencies.map((opt) => (
										<SelectItem key={opt.value} value={opt.value}>
											{opt.label}
										</SelectItem>
									))}
									{currency &&
										!availableCurrencies.some(
											(opt) => opt.value === currency,
										) && (
											<SelectItem value={currency}>
												{CURRENCY_OPTIONS.find((opt) => opt.value === currency)
													?.label ?? currency}
											</SelectItem>
										)}
								</SelectContent>
							</Select>
							{isMaster && (
								<span className="block text-[10px] text-muted-foreground pl-2">
									Master
								</span>
							)}
						</div>

						{showAmount ? (
							<div className="space-y-1">
								<div className="relative">
									<span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
										{getCurrencySymbol(currency)}
									</span>
									<Input
										type="number"
										min={0}
										step={0.01}
										value={row.amount || ""}
										onChange={(e) =>
											setField(
												"amount",
												e.target.value === "" ? 0 : Number(e.target.value),
											)
										}
										placeholder="0.00"
										className="h-9 pl-7 text-sm"
									/>
								</div>
								{eurHint(row.amount) && (
									<span className="block text-[10px] text-muted-foreground pl-2">
										{eurHint(row.amount)}
									</span>
								)}
							</div>
						) : (
							<div />
						)}

						<div className="space-y-1">
							<div className="relative">
								<span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
									{getCurrencySymbol(currency)}
								</span>
								<Input
									type="number"
									min={0}
									step={0.01}
									value={row.minimumOrderAmount ?? ""}
									onChange={(e) =>
										setField(
											"minimumOrderAmount",
											e.target.value === "" ? null : Number(e.target.value),
										)
									}
									placeholder="None"
									className="h-9 pl-7 text-sm"
								/>
							</div>
							{eurHint(row.minimumOrderAmount) && (
								<span className="block text-[10px] text-muted-foreground pl-2">
									{eurHint(row.minimumOrderAmount)}
								</span>
							)}
						</div>

						<div className="space-y-1">
							<div className="relative">
								<span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
									{getCurrencySymbol(currency)}
								</span>
								<Input
									type="number"
									min={0}
									step={0.01}
									value={row.maximumDiscountAmount ?? ""}
									onChange={(e) =>
										setField(
											"maximumDiscountAmount",
											e.target.value === "" ? null : Number(e.target.value),
										)
									}
									placeholder="No cap"
									className="h-9 pl-7 text-sm"
								/>
							</div>
							{eurHint(row.maximumDiscountAmount) && (
								<span className="block text-[10px] text-muted-foreground pl-2">
									{eurHint(row.maximumDiscountAmount)}
								</span>
							)}
						</div>

						<Button
							type="button"
							variant="ghost"
							size="icon"
							className="h-9 w-9 mt-0"
							onClick={onRemove}
							disabled={isMaster}
							title={
								isMaster
									? "EUR is the master currency and cannot be removed"
									: "Remove currency"
							}
						>
							<Trash2 className="h-4 w-4 text-muted-foreground" />
						</Button>
					</div>
				)
			}}
		</form.Subscribe>
	)
}
